import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { AnalyticsResourceType } from "@intelligems/ig-types";

import {
  getFilterMethod,
  useGraphValueFormatter,
} from "../../../api/formatters/dashboard";
import { selectedVariations } from "../../../store/slices/variations";
import { IExperienceDashboardFilters } from "../../../store/types/dashboardFilters";
import { IAppState } from "../../../store/types/store";
import theme from "../../../theme";
import { BarMetricData } from "../../../types/analytics";
import { ExperienceFe } from "../../../types/schemas/experiences";

import { GraphConfig } from "./Utils/graphConfigs";
import {
  dataMaxFormatter,
  dataMinFormatter,
  getBarSize,
  getDataMax,
  getDataMin,
  groupColors,
} from "./Utils/graphHelpers";
import {
  CustomizedAxisTick,
  CustomLabel,
  CustomResponsiveContainer,
} from "./Utils/graphStyleCustoms";

interface BarGraphProps {
  groupMetricData: BarMetricData[];
  config: GraphConfig;
  compareGroupA?: string;
  compareGroupB?: string;
  filterMethod?: string;
  resource: ExperienceFe;
  resourceType: AnalyticsResourceType;
}

export const BarGraph = ({
  groupMetricData,
  config,
  compareGroupA,
  compareGroupB,
  filterMethod,
  resource,
  resourceType,
}: BarGraphProps) => {
  const dashboardFilters: IExperienceDashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );
  const variations = useSelector(selectedVariations);
  const graphValueFormatter = useGraphValueFormatter();

  const filteredData = filterMethod
    ? getFilterMethod(groupMetricData as BarMetricData[], filterMethod)
    : groupMetricData;

  const allData =
    !filterMethod || filterMethod === "none" ? groupMetricData : filteredData;

  const dataMax = dataMaxFormatter(getDataMax(allData));
  const dataMin = dataMinFormatter(getDataMin(allData));

  const allGroupDataMapping = useMemo(() => {
    if (
      resourceType === "experiences" &&
      config.hasMetricFilterIndex &&
      dashboardFilters.statSigView === "beatControl"
    ) {
      const controlGroup = variations.find((group) => group.isControl)!;
      return allData.filter((data) => data.id !== controlGroup.id);
    } else {
      return allData;
    }
  }, [resource, dashboardFilters, config, allData, variations]);

  return (
    <CustomResponsiveContainer
      height={config.isImpactGraph ? 250 : 300}
      width="100%"
    >
      <BarChart
        style={{ marginLeft: -14 }}
        margin={{
          top: 5,
          bottom: 5,
        }}
        data={allGroupDataMapping}
      >
        <CartesianGrid
          style={{ stroke: theme.palette.disabled.light }}
          vertical={false}
        />
        <XAxis
          interval={0}
          tick={(props: any) => {
            return (
              // @ts-ignore
              <CustomizedAxisTick
                props={props}
                groupMetricData={allGroupDataMapping}
                graphValueFormatter={graphValueFormatter}
                config={config}
              />
            );
          }}
          axisLine={{ stroke: theme.palette.disabled.light }}
          tickLine={false}
          dataKey={(data) => {
            return variations.find((v) => v.id === data.id)?.name || "";
          }}
        />

        <YAxis
          domain={[dataMin, dataMax]}
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 14 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(value: any) =>
            graphValueFormatter(true, value, config)!
          }
        />
        <Tooltip
          contentStyle={{
            fontFamily: theme.typography.fontFamily,
            borderRadius: 5,
            fontSize: 14,
            backgroundColor: "rgba(97, 97, 97, 0.92)",
            color: "#FFF",
            border: "1px solid transparent",
          }}
          itemStyle={{ color: "#FFF" }}
          cursor={{ fill: "transparent" }}
          formatter={(value: any, name: any) => [
            graphValueFormatter(false, value, config)!,
            variations.find((v) => v.id === name)?.name,
          ]}
        />
        <Bar
          isAnimationActive={false}
          dataKey="value"
          name={config.title[0]}
          radius={[10, 10, 0, 0]}
          barSize={getBarSize(config.isImpactGraph, allGroupDataMapping.length)}
        >
          <LabelList
            content={(props: any) => {
              return (
                // @ts-ignore
                <CustomLabel
                  props={props}
                  config={config}
                  graphValueFormatter={graphValueFormatter}
                  compareGroupA={compareGroupA}
                  compareGroupB={compareGroupB}
                />
              );
            }}
          />

          {allGroupDataMapping?.map((group: BarMetricData, index: number) => (
            <Cell
              key={`cell-${index}`}
              fill={
                config.isImpactGraph
                  ? group.id === compareGroupA || group.id === compareGroupB
                    ? theme.palette.primary.main
                    : theme.palette.disabled.light
                  : groupColors[index]
              }
            />
          ))}
        </Bar>
      </BarChart>
    </CustomResponsiveContainer>
  );
};
